<script>
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import Spinner from 'shared/components/Spinner.vue';

const kindOf = file => {
  const type = file?.contentType || file?.content_type || '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  return 'file';
};

export default {
  name: 'CannedResponseAttachments',
  components: { Spinner },
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    // Defaults mirror the reply-message attachments; the button-reply uploader
    // passes its own tighter limits (1 file, 16MB — WhatsApp's video ceiling).
    accept: {
      type: String,
      default: 'image/*,video/mp4,application/pdf',
    },
    maxAttachments: {
      type: Number,
      default: 5,
    },
    maxFileSizeMb: {
      type: Number,
      default: 0,
    },
    addLabel: {
      type: String,
      default: '',
    },
    hint: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return { uploading: false };
  },
  computed: {
    addLabelText() {
      return this.addLabel || this.$t('CANNED_MGMT.ATTACHMENTS.ADD_BUTTON');
    },
    hintText() {
      return this.hint || this.$t('CANNED_MGMT.ATTACHMENTS.HINT');
    },
  },
  methods: {
    kindOf,
    iconOf(file) {
      return {
        image: 'i-lucide-image',
        video: 'i-lucide-video',
        file: 'i-lucide-file-text',
      }[kindOf(file)];
    },
    sizeOf(file) {
      const bytes = file.byteSize || file.byte_size || 0;
      if (!bytes) return '';
      const mb = bytes / (1024 * 1024);
      return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
    },
    async onFilesSelected(event) {
      let files = Array.from(event.target.files || []);
      // Reset so re-selecting the same file still fires @change.
      event.target.value = '';
      if (!files.length) return;

      if (this.maxFileSizeMb) {
        const limitBytes = this.maxFileSizeMb * 1024 * 1024;
        const oversized = files.filter(file => file.size > limitBytes);
        if (oversized.length) {
          useAlert(
            this.$t('CANNED_MGMT.ATTACHMENTS.FILE_TOO_BIG', {
              size: this.maxFileSizeMb,
            })
          );
          files = files.filter(file => file.size <= limitBytes);
          if (!files.length) return;
        }
      }

      const room = this.maxAttachments - this.modelValue.length;
      if (room <= 0) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENTS.MAX_COUNT_ERROR'));
        return;
      }

      this.uploading = true;
      try {
        const added = await Promise.all(
          files.slice(0, room).map(file =>
            uploadFile(file).then(({ blobId, fileUrl }) => ({
              blobSignedId: blobId,
              url: fileUrl,
              filename: file.name,
              contentType: file.type,
              byteSize: file.size,
            }))
          )
        );
        this.$emit('update:modelValue', [...this.modelValue, ...added]);
      } catch (error) {
        useAlert(this.$t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR'));
      } finally {
        this.uploading = false;
      }
    },
    removeAttachment(index) {
      const next = [...this.modelValue];
      next.splice(index, 1);
      this.$emit('update:modelValue', next);
    },
  },
};
</script>

<template>
  <div class="flex w-full flex-col gap-2">
    <div v-if="modelValue.length" class="flex flex-wrap gap-2">
      <div
        v-for="(file, index) in modelValue"
        :key="file.blobSignedId || file.blobId || index"
        class="flex max-w-full items-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 py-1 pe-1 ps-2"
      >
        <span
          class="grid size-7 shrink-0 place-items-center rounded-md"
          :class="
            kindOf(file) === 'video'
              ? 'bg-n-blue-3 text-n-blue-11'
              : kindOf(file) === 'image'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-amber-3 text-n-amber-11'
          "
        >
          <span :class="iconOf(file)" class="size-3.5" />
        </span>
        <div class="min-w-0 leading-tight">
          <a
            :href="file.url"
            target="_blank"
            rel="noopener noreferrer"
            class="block max-w-[12rem] truncate !p-0 text-[12px] font-bold text-n-slate-12 hover:text-n-brand"
            dir="ltr"
          >
            {{ file.filename }}
          </a>
          <span
            v-if="sizeOf(file)"
            class="text-[10.5px] font-medium text-n-slate-10"
            dir="ltr"
          >
            {{ sizeOf(file) }}
          </span>
        </div>
        <button
          type="button"
          class="grid size-6 shrink-0 place-items-center rounded-md text-n-slate-10 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11"
          :title="$t('CANNED_MGMT.ATTACHMENTS.REMOVE')"
          @click="removeAttachment(index)"
        >
          <span class="i-lucide-x size-3.5" />
        </button>
      </div>
    </div>

    <label
      v-if="modelValue.length < maxAttachments"
      class="flex w-fit cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-n-strong px-2.5 py-1.5 text-[12px] font-bold text-n-slate-11 transition-colors hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand"
    >
      <input
        type="file"
        multiple
        :accept="accept"
        class="hidden"
        :disabled="uploading"
        @change="onFilesSelected"
      />
      <Spinner v-if="uploading" />
      <span v-else class="i-lucide-paperclip size-3.5" />
      <span>
        {{ uploading ? $t('CANNED_MGMT.ATTACHMENTS.UPLOADING') : addLabelText }}
      </span>
    </label>
    <p class="text-[10.5px] font-medium leading-relaxed text-n-slate-10">
      {{ hintText }}
    </p>
  </div>
</template>
